import * as XLSXStyle from "xlsx-js-style";
import moment from "moment";
import {
  headerStyle,
  textStyle,
  numeroStyle,
  moneyStyle,
  dateStyle,
  horaStyle,
  fechaHoraStyle,
  totalTextoStyle,
  totalMoneyStyle,
  celdaTexto,
  celdaNumero,
  celdaMoneda,
  celdaFecha,
  celdaHora,
  celdaFechaHora,
} from "./ExcelEstilos";
import { claveDiaMexico } from "./fechaHoraMexico";

/**
 * Plomería compartida de los reportes que exportan la MISMA tabla a Excel y a PDF.
 *
 * La idea (tomada de `CreditosMovimientos/exportarCreditosMovimientos.js`) es que
 * el reporte construya una sola vez `{ columnas, filas, totales }` y de ahí salgan
 * los dos archivos, para que no puedan decir cosas distintas.
 *
 * Forma de los datos:
 *   columnas: [{ titulo, tipo, ancho, anchoPdf, enPdf }]
 *   filas:    [[{ valor, tipo }]]     paralelo a `columnas`
 *   totales:  [null | { valor, tipo }] paralelo a `columnas`
 *
 * `tipo`: "texto" | "identificador" | "numero" | "moneda" | "fecha" | "hora" |
 * "fechaHora" | "centro". "hora" y "fechaHora" reciben un instante y siempre se
 * leen en hora de México (ver `fechaHoraMexico.js`).
 */

export const texto = (valor) => ({
  valor: valor === undefined || valor === null ? "" : valor,
  tipo: "texto",
});
/**
 * Codigos, claves, codigos de barras, cuentas: cosas que se ven numericas pero
 * son identificadores. Van como TEXTO explicito porque `celdaTexto` pasa el valor
 * crudo, y un codigo de 11 digitos que llega como number sale en notacion
 * cientifica (`1.23457E+10`), lo que rompe busquedas y cruces contra otros
 * archivos. El `numFmt: "@"` ademas evita que Excel lo reinterprete si el usuario
 * edita la celda despues de descargar.
 */
export const identificador = (valor) => ({ valor, tipo: "identificador" });
export const numero = (valor) => ({ valor, tipo: "numero" });
export const moneda = (valor) => ({ valor, tipo: "moneda" });
export const fecha = (valor) => ({ valor, tipo: "fecha" });
/**
 * Para instantes (createdAt...), no para fechas-día: el día se toma en hora de
 * México y no en la del navegador, así que un registro de las 23:30 no se corre
 * al día siguiente. Se entrega como "YYYY-MM-DD" para seguir siendo una "fecha"
 * normal en el Excel y en el PDF.
 */
export const fechaMexico = (instante) => ({ valor: claveDiaMexico(instante), tipo: "fecha" });
export const horaMexico = (instante) => ({ valor: instante, tipo: "hora" });
export const fechaHoraMexico = (instante) => ({ valor: instante, tipo: "fechaHora" });
export const centro = (valor) => ({
  valor: valor === undefined || valor === null ? "" : valor,
  tipo: "centro",
});

/** Suma por columna; sólo donde el tipo declarado es dinero. */
export const totalizar = (columnas, filas) =>
  columnas.map((columna, indice) => {
    if (columna.tipo !== "moneda") return null;
    const suma = filas.reduce((acumulado, fila) => {
      const valor = Number(fila[indice] && fila[indice].valor);
      return acumulado + (Number.isFinite(valor) ? valor : 0);
    }, 0);
    return { valor: Math.round(suma * 100) / 100, tipo: "moneda" };
  });

const identificadorStyle = {
  ...textStyle,
  numFmt: "@",
};

const ESTILO_POR_TIPO = {
  moneda: moneyStyle,
  numero: numeroStyle,
  fecha: dateStyle,
  hora: horaStyle,
  fechaHora: fechaHoraStyle,
  centro: dateStyle,
  identificador: identificadorStyle,
};

/**
 * Resaltes de renglón, pedidos por nombre desde la celda:
 * `{ ...moneda(x), resalte: "nota" }`.
 *
 * Un resalte SÓLO aporta `fill` y `font`. El `numFmt`, el borde y la alineación
 * los sigue poniendo el tipo de la celda, para que un importe resaltado no
 * pierda el formato de Contabilidad (el `numFmt` es justo lo que se rompe si se
 * sustituye el estilo completo).
 */
export const RESALTES = {
  // Documento en negativo (nota de crédito): fondo rosa y texto rojo, mismo
  // lenguaje visual que el papel de trabajo de los reportes de retenciones.
  nota: {
    fill: { fgColor: { rgb: "FDE7E9" } },
    font: { bold: true, sz: 10, color: { rgb: "A8071A" } },
  },

  // Catalogo de insumos con sus equivalencias: el insumo va en turquesa Biwo y
  // sus equivalencias en ambar. Los sufijos A/B son el mismo color en dos tonos
  // y alternan por BLOQUE (por insumo, no por renglon), para que se vea donde
  // empieza el siguiente insumo sin tener que leer la columna Tipo.
  insumoA: {
    fill: { fgColor: { rgb: "BFEFF5" } },
    font: { bold: true, sz: 10, color: { rgb: "00505C" } },
  },
  insumoB: {
    fill: { fgColor: { rgb: "DFF7FA" } },
    font: { bold: true, sz: 10, color: { rgb: "00505C" } },
  },
  equivalenciaA: {
    fill: { fgColor: { rgb: "FFF1D6" } },
    font: { sz: 10, color: { rgb: "874D00" } },
  },
  equivalenciaB: {
    fill: { fgColor: { rgb: "FFF9EC" } },
    font: { sz: 10, color: { rgb: "874D00" } },
  },

  // Estados de resultados y demas reportes con subtotales intercalados: el
  // subtotal cierra un bloque y la utilidad cierra una seccion completa, de ahi
  // los dos tonos del turquesa Biwo.
  subtotal: {
    fill: { fgColor: { rgb: "EAF6F8" } },
    font: { bold: true, sz: 10, color: { rgb: "00505C" } },
  },
  utilidad: {
    fill: { fgColor: { rgb: "C9EEF3" } },
    font: { bold: true, sz: 11, color: { rgb: "003D47" } },
  },
};

/**
 * `colorTexto` tiñe SOLO la letra de una celda, encima de su tipo y de su
 * resalte: se usa para los importes en negativo de los reportes financieros.
 * Va como hex CSS ("#CF1322") porque el mismo campo lo consume `reporteFiscalPdf`;
 * aquí se le quita el "#", que es como lo quiere xlsx-js-style.
 */
const conColorTexto = (estilo, colorTexto) => {
  if (!colorTexto) return estilo;
  return {
    ...estilo,
    font: { ...(estilo.font || {}), color: { rgb: colorTexto.replace("#", "") } },
  };
};

const celdaDeDato = (celda) => {
  const base = ESTILO_POR_TIPO[celda.tipo] || textStyle;
  const resalte = celda.resalte && RESALTES[celda.resalte];
  const estilo = conColorTexto(resalte ? { ...base, ...resalte } : base, celda.colorTexto);
  if (celda.tipo === "moneda") return celdaMoneda(celda.valor, estilo);
  if (celda.tipo === "numero") return celdaNumero(celda.valor, estilo);
  if (celda.tipo === "fecha") return celdaFecha(celda.valor, estilo);
  if (celda.tipo === "hora") return celdaHora(celda.valor, estilo);
  if (celda.tipo === "fechaHora") return celdaFechaHora(celda.valor, estilo);
  // `t: "s"` explicito: xlsx-js-style no infiere el tipo cuando ya se le entrega
  // un objeto {v,s} armado, y sin el un codigo numerico vuelve a salir como
  // numero pese al numFmt.
  if (celda.tipo === "identificador") {
    return {
      v: celda.valor === undefined || celda.valor === null ? "" : String(celda.valor),
      t: "s",
      z: "@",
      s: estilo,
    };
  }
  return celdaTexto(celda.valor, estilo);
};

const tituloStyle = { font: { bold: true, sz: 13 } };
const subtituloStyle = { font: { bold: true, sz: 11, color: { rgb: "005F6B" } } };
const metaStyle = { font: { sz: 9, color: { rgb: "8C8C8C" } } };

// Las 4 líneas de encabezado del documento más la fila en blanco dejan los
// encabezados de la tabla en la fila 6.
const FILA_ENCABEZADOS = 6;

/**
 * Una hoja de Excel a partir de una tabla ya construida.
 *
 * El `!autofilter` va con el rango explícito encabezado -> última fila de datos:
 * `xlsx-js-style` estira un rango de una sola fila hasta el final de la hoja y se
 * traga la fila de totales. (Congelar paneles no se puede: `!freeze` no escribe
 * nada en esta versión de la librería.)
 */
export const construirHojaTabla = (
  { columnas, filas, totales },
  // `registros`: cuántos renglones son datos, cuando el reporte agrega su propio renglón
  // de totales dentro de `filas` (por omisión, todas las filas).
  { titulo, empresaNombre, descripcionFiltros, sinAutofiltro, registros }
) => {
  const aoa = [
    [celdaTexto(empresaNombre || "", tituloStyle)],
    [celdaTexto(titulo || "", subtituloStyle)],
    [celdaTexto(descripcionFiltros || "", metaStyle)],
    [celdaTexto(
      `Generado ${moment().format("DD/MM/YYYY HH:mm")} - ${registros ?? filas.length} registro(s)`,
      metaStyle
    )],
    [],
    columnas.map((columna) => celdaTexto(columna.titulo, headerStyle)),
    ...filas.map((fila) => fila.map(celdaDeDato)),
  ];

  if (filas.length > 0 && (totales || []).some(Boolean)) {
    aoa.push(
      columnas.map((columna, indice) => {
        if (indice === 0) return celdaTexto("TOTAL", totalTextoStyle);
        const total = totales[indice];
        if (!total) return celdaTexto("", totalTextoStyle);
        return celdaMoneda(total.valor, totalMoneyStyle);
      })
    );
  }

  const hoja = XLSXStyle.utils.aoa_to_sheet(aoa);
  hoja["!cols"] = columnas.map((columna) => ({ wch: columna.ancho || 16 }));

  // `sinAutofiltro` es para tablas con subtotales intercalados (un estado de
  // resultados, por ejemplo): al filtrar, los subtotales dejan de corresponder a
  // los renglones visibles y la hoja miente.
  if (filas.length > 0 && !sinAutofiltro) {
    const ultimaColumna = XLSXStyle.utils.encode_col(columnas.length - 1);
    hoja["!autofilter"] = {
      ref: `A${FILA_ENCABEZADOS}:${ultimaColumna}${FILA_ENCABEZADOS + filas.length}`,
    };
  }

  return hoja;
};

/** Excel no acepta : \ / ? * [ ] en el nombre de una hoja, y topa en 31 caracteres. */
export const nombreHoja = (titulo) => (titulo || "Hoja").replace(/[:\\/?*[\]]/g, " ").slice(0, 31);

/** Nombre de archivo sin acentos ni caracteres que molesten al sistema de archivos. */
export const limpiarNombreArchivo = (valor) =>
  (valor || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-zA-Z0-9_-]+/g, "_")
    .replace(/^_+|_+$/g, "");

/**
 * La misma tabla, sólo con las columnas que caben en el PDF (LETTER horizontal).
 * Una columna marcada `enPdf: false` sale del Excel pero no del PDF: los UUID de
 * 36 caracteres no caben junto a todo lo demás.
 */
export const paraPdf = ({ columnas, filas, totales }) => {
  const indices = columnas
    .map((columna, indice) => (columna.enPdf === false ? -1 : indice))
    .filter((indice) => indice >= 0);
  return {
    columnas: indices.map((indice) => columnas[indice]),
    filas: filas.map((fila) => indices.map((indice) => fila[indice])),
    totales: indices.map((indice) => (totales || [])[indice] || null),
  };
};

const armarLibro = (hojas) => {
  const libro = XLSXStyle.utils.book_new();
  for (const { nombre, hoja } of hojas) {
    XLSXStyle.utils.book_append_sheet(libro, hoja, nombreHoja(nombre));
  }
  return libro;
};

/** Escribe el libro con las hojas dadas: `[{ nombre, hoja }]`. */
export const descargarLibro = (hojas, nombreArchivo) => {
  XLSXStyle.writeFile(armarLibro(hojas), `${nombreArchivo}.xlsx`);
};

/** El mismo libro como Blob, para meterlo a un ZIP en vez de bajarlo suelto. */
export const construirLibroBlob = (hojas) => {
  const buffer = XLSXStyle.write(armarLibro(hojas), { bookType: "xlsx", type: "array" });
  return new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
};
